import { useState } from "react";
import { toast } from "sonner";
import axios, { API } from "@/lib/api";
import { formatApiError } from "@/lib/apiError";
import { X, ArrowUpRight, ArrowDownLeft, Loader2 } from "lucide-react";

export default function TransferDialog({ workspaceId, member, leadBalance, onClose, onDone }) {
  const [mode, setMode] = useState("push");  // push | recall
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const n = Number(amount);
    if (!n || n <= 0) { toast.error("Enter a positive amount."); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/workspaces/${workspaceId}/members/${member.id}/credits`,
        { amount: mode === "push" ? n : -n },
      );
      toast.success(mode === "push"
        ? `Pushed ${n} credits to ${member.email}.`
        : `Recalled ${n} credits from ${member.email}.`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err, "Transfer failed."));
    } finally {
      setSubmitting(false);
    }
  };

  const cap = mode === "push" ? leadBalance : member.credits_balance;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="team-transfer-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm rounded-2xl bg-[#0E0E0E] border border-white/10 p-7"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full border border-white/10 text-white/55 hover:text-white flex items-center justify-center"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <h2 className="font-display text-2xl text-white">Move credits</h2>
        <p className="text-sm text-white/55 mt-1">{member.email}</p>

        <div className="mt-5 inline-flex p-0.5 rounded-full border border-white/10 bg-[#141414]">
          {[
            { v: "push", label: "Push", icon: ArrowUpRight },
            { v: "recall", label: "Recall", icon: ArrowDownLeft },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setMode(opt.v)}
              data-testid={`team-transfer-${opt.v}`}
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition ${
                mode === opt.v ? "bg-[#D4AF37] text-black" : "text-white/55 hover:text-white"
              }`}
            >
              <opt.icon className="w-3 h-3" /> {opt.label}
            </button>
          ))}
        </div>

        <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mt-5 mb-1.5">
          Amount · max {cap.toFixed(cap % 1 === 0 ? 0 : 1)}
        </label>
        <input
          type="number"
          min={0}
          max={cap}
          step={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          data-testid="team-transfer-amount"
          className="w-full bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
        />
        <button
          onClick={submit}
          disabled={submitting || !amount || Number(amount) > cap}
          data-testid="team-transfer-submit"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-2.5 hover:bg-[#F3CE5A] transition disabled:opacity-60"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {mode === "push" ? "Push credits" : "Recall credits"}
        </button>
      </div>
    </div>
  );
}
